var _sentryCollisionFreeGlobalObject = typeof window != "undefined" ? window : typeof global != "undefined" ? global : typeof self != "undefined" ? self : {};
_sentryCollisionFreeGlobalObject["__sentryRewritesTunnelPath__"] = undefined;
_sentryCollisionFreeGlobalObject["SENTRY_RELEASE"] = {"id":"rAVWx_w9wfFafV7eFKzP_"};
_sentryCollisionFreeGlobalObject["__sentryBasePath"] = undefined;
_sentryCollisionFreeGlobalObject["__rewriteFramesAssetPrefixPath__"] = "/app/7";

// This file configures the initialization of Sentry on the browser.
// The config you add here will be used whenever a page is visited.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

//import * as Sentry from '@sentry/nextjs';

//const SENTRY_DSN = process.env.NEXT_PUBLIC_SENTRY;

import * as Sentry from '@sentry/nextjs';

/**
 * PayPal classic (v5) SDK teardown noise — not a failure, and not actionable.
 *
 * The v5 SDK allows a single live instance per namespace. When a second `sdk/js`
 * script executes while `window.paypal` already holds one (e.g. the shopper goes
 * from a PDP quoting EUR to a checkout quoting GBP, so the script URL differs),
 * the new instance calls `__internal_destroy__` on the old one. That destroys
 * every zoid component the old instance had registered, rejecting each with
 * `Error: zoid destroyed all components`. Nothing catches those rejections, so
 * the SDK's own handler logs `paypal_js_sdk_v5_unhandled_exception` and rethrows
 * globally, which is what lands here.
 *
 * Verified by reproduction: the Pay Later message rendered by the FIRST instance
 * still resolves, and the new instance renders normally — the shopper sees the
 * banner either way. Only the discarded instance's promises reject.
 *
 * `src/utils/paypal-pay-later.ts` keeps every surface on one options builder so
 * the SDK is not reloaded needlessly; this filter covers the reloads that are
 * legitimate (the parameters genuinely differ) and therefore cannot be removed.
 */
const PAYPAL_SDK_TEARDOWN_NOISE = [
  /zoid destroyed all components/,
  /New SDK instance loaded, existing instance destroyed/,
];

/**
 * GTM container tag noise — thrown by third-party Custom HTML tags, not by this app.
 *
 * These errors originate inside tags published in the GTM container, which is
 * shared by www.b-parts.com, www.b-parts.co.uk and reusedparts-servicebox.com
 * (all three build with `${KEY_GTM}`). Container versions publish independently
 * of our CI, so this noise appears and disappears without any deploy on our side.
 *
 * `jQuery`/`$`: the project has no jQuery dependency at all (Next + React only),
 * so these tags are legacy leftovers that cannot work regardless of our code —
 * nothing here is fixable in the app.
 *
 * `fbq`: the Meta base pixel tag had not run yet when an event tag fired. Partly
 * ours — `src/components/providers/gtm/index.tsx` pushes the `gtm.js` event in
 * `onLoad`, i.e. after the container drained the dataLayer queue, so the async
 * PDP events can be processed before Page View. Filtering here hides the Sentry
 * noise but NOT the event loss: verify pixel coverage in Meta Events Manager,
 * not here.
 *
 * All of these share one mechanism: a Custom HTML tag that fails before defining
 * the global a later tag depends on. The traces show them arriving from datacenter
 * IPs (47.128.0.0/16, AWS Singapore) with spoofed Android 5.0 / old-Chrome user
 * agents, where tags written in modern syntax fail to parse outright. The stack
 * enters through our `next/script` onLoad handler (`initializeGTMEvents`), which
 * is the trigger for tag execution, not the fault.
 *
 * Note: `denyUrls` is unreliable for these — the Next.js SDK rewrites every
 * absolute frame URL to `app:///gtm.js`, so a googletagmanager.com pattern never
 * matches. Filter on the message instead.
 */
const GTM_TAG_NOISE = [
  ///jQuery is not defined/,
  ///\$ is not defined/,
  ///fbq is not defined/,
  ///PixelManagerOptions is not defined/,
];

/**
 * `Cannot read properties of undefined (reading 'M_ID')` — código que não é nosso.
 *
 * Chega em rajadas: 14 eventos em ~2 s num único carregamento de PDP (medido numa
 * sessão `/nl`), o que por si só é a maior parte do custo deste erro.
 *
 * A propriedade `M_ID` não existe em NADA que nós controlemos. Procurada em `src/`, em
 * `node_modules/`, no bundle construído (`.next/static` e `.next/server/chunks`), nos
 * dois containers do GTM (o de dev e o `GTM-KVP9RX9` de produção), no `sdk/js` v5 e no
 * `web-sdk/v6/core` do PayPal, no loader do Klarna, no `fbevents.js`, no
 * `accounts.google.com/gsi/client`, no `analytics.js` e no `ec.js`: zero ocorrências.
 * Logo o nosso código não pode produzir este erro, e é por isso que aqui — ao contrário
 * de um nome de propriedade genérico — filtrar pela mensagem não tapa bugs nossos.
 *
 * A origem mais provável é uma extensão do browser (um `M_ID` a ser lido de um objeto
 * inexistente tem a forma de "merchant id" de uma extensão de cupões) ou um script
 * carregado por um tag do GTM. O facto de a mensagem chegar LEGÍVEL é o que aponta para
 * fora do nosso bundle sem ser cross-origin puro: um script de outro domínio sem CORS
 * daria `Script error.` sem detalhe.
 *
 * O padrão cobre as três frases possíveis — V8 diz `reading 'M_ID'`, o WebKit
 * `evaluating 'x.M_ID'` e o Gecko `can't access property "M_ID"` — por isso casa com o
 * nome da propriedade e não com a frase de um motor só.
 */
const EXTENSION_NOISE = [/\bM_ID\b/];

if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
  Sentry.init({
    normalizeDepth: 6,
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
    tracesSampleRate: 1.0,
    ignoreErrors: [
      ...PAYPAL_SDK_TEARDOWN_NOISE,
      ...GTM_TAG_NOISE,
      ...EXTENSION_NOISE,
    ],
    beforeSend: (event, hint) => {
      if (hint.originalException instanceof Error) {
        return event;
      }

      return null;
    },
  });
}
